/* ============================================================
   PST Pool Finder — collection quiz + compare drawer

   The guided 4-step quiz writes Shopify storefront filter params and, once
   done, hands off to the theme's NATIVE faceted filtering (assets/facets.js)
   via FacetFiltersForm.renderPage() — so the results + left sidebar behave
   exactly like every other collection. DURING the quiz a light fetch reads the
   live match count + per-value facet counts (to hide quiz options that would
   yield 0). Compare is localStorage-backed.
   ============================================================ */
(function () {
  'use strict';

  // Wizard size groups → member pool_size values (mirrors the Search & Discovery
  // "Pool Size" grouping). Overridable per-section via data-size-groups (JSON).
  var DEFAULT_SIZE_GROUPS = {
    small: ['8 ft', '12 ft', '15 ft', '8x12 ft', '15x10 ft', '16x10 ft'],
    medium: ['16 ft', '18 ft', '21 ft', '18x12 ft', '20x12 ft', '24x12 ft'],
    large: ['24 ft', '27 ft', '24x15 ft', '24x16 ft', '25x16 ft', '26x15 ft', '27x15 ft',
            '28x12 ft', '28x16 ft', '30x15 ft', '31x15 ft', '32x12 ft', '32x16 ft', '33x18 ft'],
    xl: ['28 ft', '30 ft', '32 ft', '33 ft', '34x18 ft', '36x18 ft', '38x18 ft', '39x18 ft', '40x16 ft', '41x21 ft']
  };

  var ORDER = ['shape', 'size', 'wall', 'material'];

  // metafield filter keys (Search & Discovery custom.* metafields)
  var MF = {
    shape: 'filter.p.m.custom.pool_shape',
    size: 'filter.p.m.custom.pool_size',
    wall: 'filter.p.m.custom.wall_height',
    material: 'filter.p.m.custom.top_rail_material'
  };

  // The store groups pool_size in Search & Discovery, so its facet values are
  // opaque group ids labelled "Small (…)", "Medium (…)", "Large (…)",
  // "Extra large (…)". Map each wizard size bucket to its group-label prefix.
  var SIZE_LABEL_PREFIX = { small: 'small', medium: 'medium', large: 'large', xl: 'extra' };

  /* ----------------------------------------------------------
     QUIZ custom element
     ---------------------------------------------------------- */
  class PstPoolFinder extends HTMLElement {
    connectedCallback() {
      // guard against double-init on theme-editor section reload
      if (this._init) return;
      this._init = true;

      this.sizeGroups = this.safeJSON(this.getAttribute('data-size-groups')) || DEFAULT_SIZE_GROUPS;
      this.resultsEl = document.querySelector('[data-pst-results]');
      this.resultsSectionId = this.resultsEl ? this.resultsEl.getAttribute('data-section-id') : null;

      this.answers = { shape: null, size: null, wall: null, material: null };
      this.step = 0;
      this.done = false;

      this.hydrateFromUrl();
      // A shared/refreshed filtered URL is already server-rendered by the native
      // facets, so we must NOT re-run renderPage on that first paint.
      this._initialDone = this.done;

      this.bind();
      this.render();
      this.refreshLiveCount();
      this.readFacets(this.resultsEl);
      this.applyAvailability();
    }

    safeJSON(s) { try { return s ? JSON.parse(s) : null; } catch (e) { return null; } }

    bind() {
      var self = this;
      this.addEventListener('click', function (e) {
        var opt = e.target.closest('[data-pst-opt]');
        if (opt) { self.pick(opt.getAttribute('data-key'), opt.getAttribute('data-val')); return; }
        if (e.target.closest('[data-pst-back]')) { self.back(); return; }
        if (e.target.closest('[data-pst-skip]')) { self.finish(); return; }
        if (e.target.closest('[data-pst-startover]')) { self.startOver(); return; }
      });
    }

    /* ---- URL <-> state ---- */
    hydrateFromUrl() {
      var p = new URLSearchParams(window.location.search);
      var self = this;
      if (p.get(MF.shape)) this.answers.shape = p.get(MF.shape).toLowerCase();
      if (p.get(MF.wall)) this.answers.wall = p.get(MF.wall);
      if (p.get(MF.material)) this.answers.material = p.get(MF.material).toLowerCase();
      var sizes = p.getAll(MF.size);
      if (sizes.length) {
        Object.keys(this.sizeGroups).forEach(function (g) {
          var members = self.sizeGroups[g];
          if (members.length === sizes.length && members.every(function (m) { return sizes.indexOf(m) >= 0; })) {
            self.answers.size = g;
          }
        });
      }
      // any answer present → the finder is already used → show results
      if (ORDER.some(function (k) { return self.answers[k]; })) this.done = true;
    }

    buildParams() {
      var p = new URLSearchParams();
      var a = this.answers;
      if (a.shape && a.shape !== 'any') p.set(MF.shape, this.cap(a.shape));
      if (a.size && a.size !== 'any' && this.sizeGroups[a.size]) {
        this.sizeGroups[a.size].forEach(function (s) { p.append(MF.size, s); });
      }
      if (a.wall && a.wall !== 'any') p.set(MF.wall, a.wall);
      if (a.material && a.material !== 'any') p.set(MF.material, this.cap(a.material));
      return p;
    }

    cap(s) { return s.charAt(0).toUpperCase() + s.slice(1); }

    syncUrl() {
      var cur = new URLSearchParams(window.location.search);
      var p = this.buildParams();
      var view = cur.get('view');
      if (view) p.set('view', view);
      var sort = cur.get('sort_by');
      if (sort) p.set('sort_by', sort);
      var qs = p.toString();
      var url = window.location.pathname + (qs ? '?' + qs : '');
      window.history.replaceState({}, '', url);
    }

    /* ---- quiz step flow ---- */
    pick(key, val) {
      this.answers[key] = val;
      var idx = ORDER.indexOf(key);
      if (idx >= ORDER.length - 1) {
        this.done = true;
      } else {
        var self = this;
        setTimeout(function () {
          if (self.step === idx && !self.done) { self.step = idx + 1; self.render(); }
        }, 200);
      }
      this.render();
      this.syncUrl();
      if (!this.done) this.fetchResults();
    }
    back() { this.step = Math.max(0, this.step - 1); this.render(); }
    skip() {
      this.answers = { shape: 'any', size: 'any', wall: 'any', material: 'any' };
      this.done = true;
      this.render(); this.syncUrl();
    }
    // Footer button: with matches narrowed, jump to them; otherwise (nothing
    // picked, or current filters match 0 pools) fall back to showing all pools.
    finish() {
      if (this.answeredCount() > 0 && this._liveCount > 0) {
        this.done = true;
        this.render(); this.syncUrl();
      } else {
        this.skip();
      }
    }
    updateFinishButton() {
      var label = this.querySelector('[data-pst-skip-label]');
      if (!label) return;
      var showCurrent = this.answeredCount() > 0 && this._liveCount > 0;
      label.textContent = showCurrent ? 'Show current matches' : 'Show all pools';
    }
    // "Start over" is the only path back into the quiz.
    startOver() {
      this.answers = { shape: null, size: null, wall: null, material: null };
      this.step = 0; this.done = false;
      this.render(); this.syncUrl(); this.fetchResults();
      try { this.scrollIntoView({ behavior: this.reduceMotion() ? 'auto' : 'smooth', block: 'start' }); } catch (e) {}
    }

    answeredCount() {
      var a = this.answers;
      return ORDER.filter(function (k) { return a[k]; }).length;
    }

    /* ---- rendering of quiz chrome + hand-off ---- */
    render() {
      var wizard = this.querySelector('[data-pst-wizard]');
      var head = this.querySelector('[data-pst-head]');
      var retake = this.querySelector('[data-pst-retake]');
      if (wizard) wizard.hidden = this.done;
      if (head) head.hidden = this.done;
      if (retake) retake.hidden = !this.done;
      if (this.resultsEl) this.resultsEl.hidden = !this.done;

      if (!this.done) {
        this.renderStep();
        this._resultsShown = false;
      } else if (!this._resultsShown) {
        this._resultsShown = true;
        this.applyFiltersToResults();
      }
      this.updateFinishButton();
    }

    // Hand the quiz's picks to the theme's native facets (facets.js), which
    // renders the grid + left sidebar just like every other collection.
    applyFiltersToResults() {
      if (typeof FacetFiltersForm === 'undefined' || typeof FacetFiltersForm.renderPage !== 'function') return;
      if (this._initialDone) { this._initialDone = false; return; } // server already rendered these filters
      try { FacetFiltersForm.renderPage(this.buildParams().toString(), null, true); } catch (e) {}
    }

    renderStep() {
      var self = this;
      var prev = this._animatedStep;
      var changed = prev !== undefined && prev !== this.step;
      var dir = this.step >= (prev === undefined ? this.step : prev) ? 1 : -1;
      this._animatedStep = this.step;

      this.querySelectorAll('[data-pst-step]').forEach(function (stepEl) {
        stepEl.hidden = parseInt(stepEl.getAttribute('data-pst-step'), 10) !== self.step;
      });

      var activeStep = this.querySelector('[data-pst-step="' + this.step + '"]');
      if (activeStep && changed && !this.reduceMotion()) this.animateIn(activeStep, dir);

      var fill = this.querySelector('[data-pst-progress]');
      if (fill) fill.style.width = Math.round((this.answeredCount() / 4) * 100) + '%';
      var idxLabel = this.querySelector('[data-pst-stepindex]');
      if (idxLabel) idxLabel.textContent = 'Step ' + (this.step + 1) + ' of 4';
      var back = this.querySelector('[data-pst-back]');
      if (back) back.style.visibility = this.step > 0 ? 'visible' : 'hidden';
      // Step 1 has no picks yet, so the live count is noise — hide it and
      // center the "Show all pools" link (see .pst-wizard__foot.is-first-step).
      var foot = this.querySelector('.pst-wizard__foot');
      if (foot) foot.classList.toggle('is-first-step', this.step === 0);
      if (activeStep) {
        var key = ORDER[this.step];
        activeStep.querySelectorAll('[data-pst-opt]').forEach(function (o) {
          o.setAttribute('aria-pressed', String(o.getAttribute('data-val') === self.answers[key]));
        });
      }
      this.applyAvailability();
    }

    animateIn(el, dir) {
      try {
        el.animate(
          [
            { opacity: 0, transform: 'translateX(' + (dir * 28) + 'px)' },
            { opacity: 1, transform: 'translateX(0)' }
          ],
          { duration: 320, easing: 'cubic-bezier(.22,1,.36,1)' }
        );
      } catch (e) {}
    }

    reduceMotion() {
      try { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch (e) { return false; }
    }

    /* ---- light fetch used DURING the quiz (live count + availability only;
           does NOT touch the results DOM — facets.js owns that) ---- */
    sectionUrl() {
      var cur = new URLSearchParams(window.location.search);
      var p = this.buildParams();
      var sort = cur.get('sort_by');
      if (sort) p.set('sort_by', sort);
      var base = window.location.pathname + '?section_id=' + encodeURIComponent(this.resultsSectionId);
      var view = cur.get('view');
      if (view) base += '&view=' + encodeURIComponent(view);
      var qs = p.toString();
      return base + (qs ? '&' + qs : '');
    }

    fetchResults() {
      if (!this.resultsSectionId || !this.resultsEl) return;
      var self = this;
      if (this._abort) this._abort.abort();
      this._abort = new AbortController();
      fetch(this.sectionUrl(), { signal: this._abort.signal })
        .then(function (r) { return r.text(); })
        .then(function (text) {
          var doc = new DOMParser().parseFromString(text, 'text/html');
          self.updateLiveCountFrom(doc);
          self.readFacets(doc);
          self.applyAvailability();
        })
        .catch(function (e) { /* aborted or offline — keep prior count */ });
    }

    refreshLiveCount() { this.updateLiveCountFrom(this.resultsEl); }

    updateLiveCountFrom(scope) {
      if (!scope) return;
      var node = scope.querySelector('[data-pst-result-count]');
      var n = node ? parseInt(node.getAttribute('data-pst-result-count'), 10) : null;
      if (n === null || isNaN(n)) return;
      this._liveCount = n;
      var live = this.querySelector('[data-pst-live]');
      if (live) live.textContent = n + (n === 1 ? ' pool matches so far' : ' pools match so far');
      this.updateFinishButton();
    }

    /* ---- availability: hide quiz options that would drop results to 0 ----
       Reads the per-value facet counts (each already constrained by the OTHER
       active filters). Fails open: a null count is never hidden. */
    readFacets(scope) {
      if (!scope) return;
      var node = scope.querySelector('[data-pst-facets]');
      if (!node) return;
      try { this.facets = JSON.parse(node.textContent); } catch (e) { /* keep prior facets */ }
    }

    facetCount(key, val) {
      var f = this.facets;
      if (!f || !val || val === 'any') return null;
      if (key === 'size') return this.sizeBucketCount(val);
      var group, vkey;
      if (key === 'shape') { group = f[MF.shape]; vkey = this.cap(val); }
      else if (key === 'wall') { group = f[MF.wall]; vkey = val; }
      else if (key === 'material') { group = f[MF.material]; vkey = this.cap(val); }
      else return null;
      if (!group) return null;
      return group[vkey] ? group[vkey].c : 0;
    }

    sizeBucketCount(val) {
      var f = this.facets;
      if (!f) return null;
      var group = f[MF.size];
      if (!group) return null;
      var prefix = SIZE_LABEL_PREFIX[val];
      if (!prefix) return null;
      var matched = false, sum = 0;
      Object.keys(group).forEach(function (gv) {
        var lbl = (group[gv].l || '').toLowerCase();
        if (lbl.indexOf(prefix) === 0) { matched = true; sum += (group[gv].c || 0); }
      });
      return matched ? sum : null;
    }

    optionAvailable(key, val) {
      if (!val || val === 'any') return true;
      var c = this.facetCount(key, val);
      return c == null ? true : c > 0;
    }

    applyAvailability() {
      var self = this;
      this.querySelectorAll('[data-pst-opt]').forEach(function (o) {
        var key = o.getAttribute('data-key');
        var val = o.getAttribute('data-val');
        var keep = val === 'any' || self.answers[key] === val || self.optionAvailable(key, val);
        o.classList.toggle('is-unavailable', !keep);
      });
    }
  }
  if (!customElements.get('pst-pool-finder')) customElements.define('pst-pool-finder', PstPoolFinder);

  /* ----------------------------------------------------------
     COMPARE controller (global, event-delegated)
     Pool cards carry data-pst-pool-card with a JSON payload.
     ---------------------------------------------------------- */
  var Compare = {
    KEY: 'pst:compare',
    MAX: 3,
    list: [],
    load: function () { try { this.list = JSON.parse(localStorage.getItem(this.KEY)) || []; } catch (e) { this.list = []; } },
    save: function () { try { localStorage.setItem(this.KEY, JSON.stringify(this.list)); } catch (e) {} },

    init: function () {
      this.load();
      var self = this;
      document.addEventListener('click', function (e) {
        var toggle = e.target.closest('[data-pst-compare-toggle]');
        if (toggle) { e.preventDefault(); self.toggle(toggle.getAttribute('data-pst-compare-toggle')); return; }
        if (e.target.closest('[data-pst-compare-open]')) { self.open(); return; }
        if (e.target.closest('[data-pst-compare-clear]')) { self.clear(); return; }
        if (e.target.closest('[data-pst-compare-close]')) { self.close(); return; }
        var rm = e.target.closest('[data-pst-compare-remove]');
        if (rm) { self.toggle(rm.getAttribute('data-pst-compare-remove')); self.open(); return; }
      });
      this.sync();
    },

    cardData: function (id) {
      var el = document.querySelector('[data-pst-pool-card][data-id="' + CSS.escape(id) + '"]');
      if (!el) return null;
      try { return JSON.parse(el.getAttribute('data-pst-pool-card')); } catch (e) { return null; }
    },

    toggle: function (id) {
      var i = this.list.indexOf(id);
      if (i >= 0) this.list.splice(i, 1);
      else if (this.list.length < this.MAX) this.list.push(id);
      this.save();
      this.sync();
    },
    clear: function () { this.list = []; this.save(); this.sync(); this.close(); },

    sync: function () {
      var self = this;
      document.querySelectorAll('[data-pst-compare-toggle]').forEach(function (btn) {
        var id = btn.getAttribute('data-pst-compare-toggle');
        var inList = self.list.indexOf(id) >= 0;
        btn.setAttribute('aria-pressed', String(inList));
        btn.disabled = !inList && self.list.length >= self.MAX;
        var label = btn.querySelector('[data-pst-compare-label]');
        if (label) label.textContent = inList ? 'Comparing' : 'Compare';
      });
      var bar = document.querySelector('[data-pst-compare-bar]');
      if (bar) {
        bar.hidden = this.list.length === 0;
        var lbl = bar.querySelector('[data-pst-compare-barlabel]');
        if (lbl) lbl.textContent = this.list.length + ' selected · compare up to ' + this.MAX;
      }
    },

    open: function () {
      var modal = document.querySelector('[data-pst-compare-modal]');
      if (!modal || !this.list.length) return;
      this.renderTable(modal);
      modal.hidden = false;
      document.body.classList.add('overflow-hidden');
    },
    close: function () {
      var modal = document.querySelector('[data-pst-compare-modal]');
      if (modal) modal.hidden = true;
      document.body.classList.remove('overflow-hidden');
    },

    renderTable: function (modal) {
      var self = this;
      var pools = this.list.map(function (id) { return self.cardData(id); }).filter(Boolean);
      var rows = [
        ['Series', 'series'], ['Size', 'sizeLabel'], ['Wall height', 'wall'],
        ['Water capacity', 'gallons'], ['Footprint', 'footprint'], ['Built-in step', 'step'],
        ['Top rail', 'top'], ['Frame', 'frame'], ['Price', 'priceLabel']
      ];
      var head = '<div class="pst-compare__row"><div class="pst-compare__rowhead" style="background:#fff;border:0"></div>';
      pools.forEach(function (p) {
        head += '<div class="pst-compare__colhead">' +
          '<div class="pst-compare__col-series">' + (p.series || '') + '</div>' +
          '<div class="pst-compare__col-title">' + (p.name || '') + '</div>' +
          '<div class="pst-compare__col-price">' + (p.priceLabel || '') + '</div>' +
          '<a class="pst-compare__col-config" href="' + (p.url ? p.url + '?view=pool-customizer' : '#') + '">Configure →</a>' +
          '<button type="button" class="pst-compare__col-remove" data-pst-compare-remove="' + p.id + '">Remove</button>' +
          '</div>';
      });
      head += '</div>';
      var body = '';
      rows.forEach(function (r) {
        body += '<div class="pst-compare__row"><div class="pst-compare__rowhead">' + r[0] + '</div>';
        pools.forEach(function (p) { body += '<div class="pst-compare__cell">' + (p[r[1]] != null ? p[r[1]] : '—') + '</div>'; });
        body += '</div>';
      });
      var table = modal.querySelector('[data-pst-compare-table]');
      if (table) table.innerHTML = head + body;
    }
  };
  window.PstCompare = Compare;

  function boot() { Compare.init(); }
  if (document.readyState !== 'loading') boot();
  else document.addEventListener('DOMContentLoaded', boot);
})();
